<template>
  <div
    ref="seat"
    :id="title"
    class="seat"
    :style="computedStyles"
    :class="{ flipping: isFlipping, 'show-info': showPassengerInfo }"
    @click="togglePassengerInfo"
  >
    <div class="seat__inner" @transitionend="handleTransitionEnd">
      <div class="seat__face seat__face--front">
        <i class="seat__icon fas fa-question"></i>
      </div>

      <div class="seat__face seat__face--back" :style="{ backgroundColor }">
        <i v-if="status === 'empty'" class="seat__icon fas fa-question"></i>
        <span v-if="passenger" class="seat__avatar">{{
          passengerFirstLetter
        }}</span>
      </div>
    </div>

    <div v-if="passenger && showPassengerInfo" class="passenger-info">
      {{ passenger.toUpperCase() }}
    </div>
  </div>
</template>

<script>
export default {
  name: "Seat",
  props: {
    title: {
      type: String
    },
    styles: {
      type: Object
    },
    status: {
      type: String
    },
    passenger: {
      type: String
    }
  },
  data() {
    return {
      width: 0,
      isFlipping: false,
      showPassengerInfo: false, // This will determine whether to show the tooltip after the flip
      isFlipped: false // This will track if the flip animation is done
    }
  },
  computed: {
    passengerFirstLetter() {
      return this.passenger ? this.passenger[0].toUpperCase() : ""
    },
    backgroundColor() {
      const bgColors = {
        empty: "#ecf0f1",
        awaiting: "#f1c40f",
        confirmed: "#2ecc71"
      }

      return bgColors[this.status]
    },
    computedStyles() {
      return {
        ...this.styles,
        left: parseInt(this.styles.left) - this.width / 2 + "px",
        bottom: parseInt(this.styles.bottom) - this.width / 2 + "px"
      }
    }
  },
  mounted() {
    this.width = this.$refs.seat.clientWidth

    setTimeout(() => {
      this.isFlipping = true
    }, 2000)
  },
  methods: {
    togglePassengerInfo() {
      if (window.innerWidth <= 600 && "ontouchstart" in window) {
        this.showPassengerInfo = !this.showPassengerInfo
      }
    },
    handleTransitionEnd() {
      if (event.propertyName === "transform" && this.isFlipping) {
        this.isFlipped = true
        this.showPassengerInfo = true
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.seat {
  position: absolute;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  perspective: 1000px;

  @media (min-width: 600px) {
    width: 50px;
    height: 50px;
  }

  &__icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 1.5rem;
  }

  &__avatar {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 1.5rem;
    font-weight: bold;
    color: #fff;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
  }
}

.seat__inner {
  position: relative;
  width: 100%;
  height: 100%;
  transform-style: preserve-3d;
  transition: transform 1s;
}

.flipping .seat__inner {
  transform: rotateY(180deg);
}

.seat__face {
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden; // hides the side facing away
  border-radius: 50%;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.7);
}

.seat__face--front {
  background-color: #ecf0f1;
  transform: rotateY(0deg);
}

.seat__face--back {
  transform: rotateY(180deg);
}

.passenger-info {
  position: absolute;
  top: 55px;
  left: 50%;
  background-color: darkorange;
  color: #fff;
  font-size: 0.8rem;
  font-weight: bold;
  padding: 5px 8px;
  border-radius: 5px;
  transform: translate(-50%, 0);
  z-index: 999;
  transition: opacity 0.3s;
  opacity: 0;

  &:before {
    content: "";
    position: absolute;
    top: -5px;
    left: 50%;
    transform: translateX(-50%);
    border-width: 0 5px 5px 5px;
    border-style: solid;
    border-color: transparent transparent darkorange transparent;
  }
}

.seat:hover .passenger-info,
.seat:focus .passenger-info {
  opacity: 1; // become visible on hover or focus
}

@media (min-width: 900px) {
  .seat.show-info .passenger-info {
    opacity: 1; // become visible after flip animation
  }
}

@media (max-width: 900px) {
  // Default styles for devices less than 600px
  .passenger-info {
    opacity: 0;
    transition: opacity 0.3s;
  }

  // Detecting hover on non-touch devices using hover media feature
  @media (hover: hover) {
    .seat:hover .passenger-info {
      opacity: 1;
    }
  }
}
</style>
